// Phase A.2.b · <ContactLinker> — unified Client / Tour / Organization
// relationship builder.
//
// Contract:
//   mode="client"        + customerId       → creates client_contact_relationships
//   mode="tour"          + tourId + customer→ creates tour_contacts; optional
//                                              explicit `also_link_organization`
//                                              checkbox (default OFF) when the
//                                              tour's Client has a resolvable
//                                              legal_entity_organization_id.
//   mode="organization"  + organizationId   → creates contact_organization_roles
//
// Server remains authoritative. UI does not simulate two writes on the
// tour+org path — it forwards `also_link_organization: true` on the
// tours POST so the A.2.a backend contract emits both audit records
// canonically.
//
// 409 → operator-friendly duplicate feedback + refresh.
// 422 → validation message shown; role not silently coerced.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Search, X, Loader2, UserPlus, Link2, Info } from "lucide-react";
import { contactApi, clientContactRelApi, contactRoleApi } from "@/lib/entities";
import { tourApi } from "@/lib/tourEntities";
import { CANONICAL_ROLES, roleLabel } from "@/lib/relationshipRoles";
import { ContactFormDialog } from "@/pages/contacts/ContactFormDialog";

const DEBOUNCE_MS = 220;

const CONTEXT = {
  client: { targetType: "client", noun: "Client" },
  tour: { targetType: "tour", noun: "Tour" },
  organization: { targetType: "organization", noun: "Organization" },
};

export function ContactLinker({
  mode,
  customerId,
  tourId,
  organizationId,
  customer, // used in tour mode to expose the explicit org link checkbox
  onClose,
  onLinked,
}) {
  const ctx = CONTEXT[mode];
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [role, setRole] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [searched, setSearched] = useState(false);
  const [alsoLinkOrg, setAlsoLinkOrg] = useState(false);
  const inputRef = useRef(null);

  const contextParams = useMemo(() => {
    if (mode === "client") return { exclude_linked_to_customer_id: customerId };
    if (mode === "tour") return { exclude_linked_to_tour_id: tourId };
    if (mode === "organization")
      return { exclude_linked_to_organization_id: organizationId };
    return {};
  }, [mode, customerId, tourId, organizationId]);

  const clientOrgId =
    mode === "tour" ? customer?.legal_entity_organization_id : null;
  const clientOrgName = customer?.legal_entity_organization?.display_name || "";

  useEffect(() => {
    // Autofocus on mount.
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (q.trim().length === 0) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const d = await contactApi.search({
          q,
          page_size: 25,
          ...contextParams,
        });
        if (!cancelled) {
          setResults(d.results || []);
          setSearched(true);
        }
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q, contextParams]);

  const submit = async () => {
    if (!selected || !role) {
      setError({ field: "role", message: "Select a Contact and Role." });
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (mode === "client") {
        await clientContactRelApi.create({
          customer_id: customerId,
          contact_id: selected.id,
          role,
        });
      } else if (mode === "tour") {
        const body = { contact_id: selected.id, role };
        if (alsoLinkOrg && clientOrgId) {
          body.also_link_organization = true;
          body.organization_id = clientOrgId;
          body.organization_role = role;
        }
        await tourApi.addContact(tourId, body);
      } else if (mode === "organization") {
        await contactRoleApi.attach({
          contact_id: selected.id,
          organization_id: organizationId,
          role,
        });
      }
      toast.success(`Linked to ${ctx.noun}.`);
      onLinked?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (status === 409 && detail?.code === "relationship_already_exists") {
        setError({
          code: "409",
          message: `This Contact is already linked to this ${ctx.noun}.`,
        });
        // Refresh from authoritative state.
        onLinked?.({ silent: true, refreshOnly: true });
      } else if (status === 422 && detail?.code === "invalid_role") {
        setError({
          code: "422",
          message: `"${roleLabel(
            detail.given,
          )}" is not a valid role. Choose a current role from the list.`,
        });
      } else {
        setError({
          code: String(status || "err"),
          message:
            (typeof detail === "string" && detail) ||
            detail?.message ||
            "Failed to link contact.",
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const canOfferCreate = q.trim().length >= 2;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Link Contact to ${ctx.noun}`}
      onClick={onClose}
      onKeyDown={(e) => e.key === "Escape" && onClose?.()}
      data-testid={`contact-linker-${mode}`}
    >
      <div
        className="bg-white w-full sm:max-w-lg rounded-t-md sm:rounded-md shadow-xl border border-coach-silver flex flex-col max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-carbon text-white px-4 py-3 flex items-center justify-between shrink-0">
          <div>
            <div className="text-[10.5px] uppercase tracking-widest text-coach-silver">
              {ctx.noun}
            </div>
            <div className="text-[15px] font-medium mt-0.5">
              Link Contact
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid={`contact-linker-close-${mode}`}
            aria-label="Close"
            className="text-coach-silver hover:text-white text-[12px] focus-visible:outline-2 focus-visible:outline-touring-lime px-2 py-1 rounded-sm"
          >
            Close
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto flex-1 min-h-0">
          {/* Search */}
          <label className="block">
            <span className="sr-only">Search contacts globally</span>
            <div className="relative">
              <Search
                size={13}
                className="absolute left-2 top-1/2 -translate-y-1/2 text-steel"
                aria-hidden="true"
              />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setSelected(null);
                  setError(null);
                }}
                placeholder="Search Contacts by name, email, phone…"
                data-testid={`contact-linker-search-${mode}`}
                aria-label="Search contacts globally"
                className="w-full border border-coach-silver rounded-sm pl-7 pr-8 py-2 text-[13px] focus-visible:outline-2 focus-visible:outline-coach-blue"
              />
              {loading && (
                <Loader2
                  size={13}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-steel animate-spin"
                  aria-hidden="true"
                />
              )}
            </div>
          </label>

          {/* Results */}
          {q.trim().length > 0 && !selected && (
            <ul
              className="max-h-[45vh] sm:max-h-[38vh] overflow-y-auto border border-coach-silver rounded-sm divide-y divide-coach-silver/60"
              data-testid={`contact-linker-results-${mode}`}
            >
              {!loading && results.length === 0 && (
                <li className="px-3 py-4 text-[12.5px] text-steel">
                  No contacts match “{q}”.
                </li>
              )}
              {results.map((r) => (
                <ResultRow
                  key={r.id}
                  row={r}
                  mode={mode}
                  onSelect={() => {
                    if (isAlreadyLinked(r, mode)) return;
                    setSelected(r);
                  }}
                />
              ))}
            </ul>
          )}

          {/* Selected + Role + Explicit-Org */}
          {selected && (
            <div className="border border-coach-blue bg-coach-blue/5 rounded-sm p-3 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10.5px] uppercase tracking-widest text-coach-blue">
                    Selected Contact
                  </div>
                  <div className="text-[14px] font-medium text-carbon truncate">
                    {contactName(selected)}
                  </div>
                  <div className="text-[11.5px] text-steel truncate">
                    {[selected.primary_email, selected.mobile]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </div>
                  {selected.primary_organization_name && (
                    <div className="text-[11.5px] text-steel truncate">
                      {selected.primary_organization_name}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => {
                    setSelected(null);
                    setError(null);
                  }}
                  data-testid={`contact-linker-clear-selection-${mode}`}
                  aria-label="Clear selection"
                  className="text-steel hover:text-carbon"
                >
                  <X size={14} />
                </button>
              </div>

              <label className="block">
                <div className="text-[10.5px] uppercase tracking-widest text-steel mb-1">
                  Role <span className="text-critical-red">*</span>
                </div>
                <select
                  value={role}
                  onChange={(e) => {
                    setRole(e.target.value);
                    setError(null);
                  }}
                  data-testid={`contact-linker-role-${mode}`}
                  aria-label="Role"
                  className="w-full border border-coach-silver rounded-sm px-2 py-2 text-[13px] bg-white focus-visible:outline-2 focus-visible:outline-coach-blue"
                >
                  <option value="">— Choose a role —</option>
                  {CANONICAL_ROLES.map(([k, l]) => (
                    <option key={k} value={k}>
                      {l}
                    </option>
                  ))}
                </select>
              </label>

              {mode === "tour" && clientOrgId && (
                <label
                  className="flex items-start gap-2 text-[12.5px] text-carbon cursor-pointer"
                  data-testid="contact-linker-also-link-org-label"
                >
                  <input
                    type="checkbox"
                    checked={alsoLinkOrg}
                    onChange={(e) => setAlsoLinkOrg(e.target.checked)}
                    data-testid="contact-linker-also-link-org"
                    className="mt-0.5"
                  />
                  <span>
                    Also link this Contact to{" "}
                    <span className="font-medium">
                      {clientOrgName || "the Client's Organization"}
                    </span>{" "}
                    as an Organization relationship.
                    <span className="block text-[11px] text-steel">
                      Off by default. When ON, writes both the Tour Contact
                      and the Organization role in one canonical save.
                    </span>
                  </span>
                </label>
              )}
            </div>
          )}

          {/* Error feedback */}
          {error && (
            <div
              className="border border-critical-red bg-critical-red/10 rounded-sm p-2 text-[12.5px] text-critical-red flex gap-2"
              data-testid={`contact-linker-error-${mode}`}
              role="alert"
            >
              <Info size={14} className="shrink-0 mt-0.5" aria-hidden="true" />
              <span>{error.message}</span>
            </div>
          )}

          {/* Create-new footer */}
          {searched && canOfferCreate && !selected && (
            <div className="pt-1">
              <button
                type="button"
                onClick={() => setCreateOpen(true)}
                data-testid={`contact-linker-create-new-${mode}`}
                className="inline-flex items-center gap-1.5 text-[12.5px] text-coach-blue hover:underline focus-visible:outline-2 focus-visible:outline-coach-blue rounded-sm"
              >
                <UserPlus size={12} /> Create New Contact
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-coach-silver bg-road-white px-4 py-3 flex items-center justify-end gap-2 shrink-0">
          <button
            onClick={onClose}
            data-testid={`contact-linker-cancel-${mode}`}
            className="border border-coach-silver text-steel text-[12.5px] px-3 py-1.5 rounded-sm hover:bg-white focus-visible:outline-2 focus-visible:outline-coach-blue"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!selected || !role || saving}
            data-testid={`contact-linker-submit-${mode}`}
            className="inline-flex items-center gap-1.5 bg-coach-blue hover:bg-deep-road-navy text-white text-[12.5px] font-medium px-4 py-1.5 rounded-sm disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-touring-lime"
          >
            <Link2 size={12} />
            {saving ? "Linking…" : `Link to ${ctx.noun}`}
          </button>
        </div>
      </div>

      {createOpen && (
        <ContactFormDialog
          open={createOpen}
          defaultCustomer={mode === "tour" ? customer : undefined}
          onClose={() => setCreateOpen(false)}
          onSaved={(contact) => {
            setCreateOpen(false);
            if (contact?.id) {
              setSelected(contact);
              setQ(`${contact.first_name || ""} ${contact.last_name || ""}`.trim());
            }
          }}
        />
      )}
    </div>
  );
}

function isAlreadyLinked(row, mode) {
  if (mode === "organization") return !!row.already_linked_to_this_organization;
  return !!row.already_linked_here;
}

function contactName(c) {
  return (
    `${c.preferred_name || c.first_name || ""} ${c.last_name || ""}`.trim() ||
    "(no name)"
  );
}

function ResultRow({ row, mode, onSelect }) {
  const linked = isAlreadyLinked(row, mode);
  const linkedRole =
    mode === "organization"
      ? row.already_linked_role
      : linked
        ? "linked"
        : null;

  return (
    <li
      className={`px-3 py-2 flex items-start justify-between gap-2 ${
        linked ? "bg-chrome-mist/40" : "hover:bg-road-white cursor-pointer"
      }`}
      onClick={linked ? undefined : onSelect}
      data-testid={`contact-linker-result-${row.id}`}
      role={linked ? undefined : "button"}
      tabIndex={linked ? -1 : 0}
      onKeyDown={(e) => {
        if (linked) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-medium text-carbon truncate">
          {contactName(row)}
          {row.title && (
            <span className="ml-2 text-[11.5px] font-normal text-steel">
              · {row.title}
            </span>
          )}
        </div>
        <div className="text-[11.5px] text-steel truncate">
          {[row.primary_email, row.mobile].filter(Boolean).join(" · ") || "—"}
        </div>
        {row.primary_organization_name && (
          <div className="text-[11.5px] text-steel truncate">
            {row.primary_organization_name}
          </div>
        )}
        <div
          className={`mt-1 text-[11px] uppercase tracking-widest ${
            linked ? "text-amber-hold" : "text-touring-lime"
          }`}
          data-testid={`contact-linker-indicator-${row.id}`}
        >
          {linked
            ? mode === "organization" && linkedRole
              ? `Already linked · Role: ${roleLabel(linkedRole)}`
              : `Already linked to this ${CONTEXT[mode].noun}`
            : `Not linked to this ${CONTEXT[mode].noun}`}
        </div>
      </div>
      <span
        className={`shrink-0 text-[11.5px] font-medium ${
          linked ? "text-steel" : "text-coach-blue"
        }`}
      >
        {linked ? "Already Linked" : "Select →"}
      </span>
    </li>
  );
}

export default ContactLinker;
